/**
 * Профайлер для ConfigKernelLoader
 */

import { EyeOutlined, ReloadOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons'
import { App, Button, Input, Modal, Select, Space, Table, Typography } from 'antd'
import React, { useMemo, useState } from 'react'
import { Kernel } from '../../../Kernel'
import { DebugProfilerData, DebugProfilerInterface } from '../types'
import { JsonViewer } from '../components/JsonViewer'

const { Text } = Typography

// Компонент для відображення JSON у модальному вікні
const JsonModal: React.FC<{ value: any; title: string }> = ({ value, title }) => {
    const [isVisible, setIsVisible] = useState(false)
    const { message } = App.useApp()

    const jsonStr = JSON.stringify(value, null, 2)

    const copyToClipboard = () => {
        navigator.clipboard
            .writeText(jsonStr)
            .then(() => {
                message.success('JSON скопійовано!')
            })
            .catch((err) => {
                console.error('Помилка копіювання:', err)
            })
    }

    return (
        <>
            <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => setIsVisible(true)}
                style={{ padding: 0, height: 'auto' }}
            >
                JSON ({Math.round((jsonStr.length / 1024) * 100) / 100} KB)
            </Button>
            <Modal
                title={`Конфігурація: ${title}`}
                open={isVisible}
                onCancel={() => setIsVisible(false)}
                footer={[
                    <Button key="copy" onClick={copyToClipboard}>
                        Копіювати
                    </Button>,
                    <Button key="close" type="primary" onClick={() => setIsVisible(false)}>
                        Закрити
                    </Button>,
                ]}
                width={900}
                styles={{ body: { padding: '16px' } }}
            >
                <JsonViewer data={value} showSearch={true} />
            </Modal>
        </>
    )
}

// Компонент для відображення конфігурацій з фільтрами
const ConfigsTable: React.FC<{ configs: any; onRefresh?: () => void }> = ({ configs, onRefresh }) => {
    const [searchText, setSearchText] = useState('')
    const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined)
    const [activeTab, setActiveTab] = useState<'all' | 'system' | 'user'>('all')

    // Перетворюємо конфігурації в масив для відображення
    const configsArray = useMemo(() => {
        const allConfigs: any[] = []

        // Додаємо системні конфігурації
        ;['app', 'api', 'features', 'theme', 'locale'].forEach((key) => {
            if (configs[key]) {
                allConfigs.push({
                    key,
                    name: key,
                    category: 'system',
                    type: typeof configs[key],
                    hasValue: true,
                    value: configs[key],
                    source: 'base + env',
                })
            }
        })

        // Додаємо користувацькі конфігурації
        Object.entries(configs).forEach(([key, value]) => {
            if (!['app', 'api', 'features', 'theme', 'locale'].includes(key)) {
                allConfigs.push({
                    key,
                    name: key,
                    category: 'user',
                    type: typeof value,
                    hasValue: value !== undefined && value !== null,
                    value: value,
                    source: 'user config',
                })
            }
        })

        return allConfigs
    }, [configs])

    // Отримуємо унікальні значення для фільтрів
    const categories = useMemo(() => {
        return ['system', 'user']
    }, [])

    // Фільтрація даних
    const filteredConfigs = useMemo(() => {
        return configsArray.filter((config) => {
            // Пошук за підрядком в імені
            const searchMatch = searchText === '' || config.name.toLowerCase().includes(searchText.toLowerCase())

            // Фільтр за категорією
            const categoryMatch = categoryFilter === undefined || config.category === categoryFilter

            // Фільтр за табом
            const tabMatch = activeTab === 'all' || config.category === activeTab

            return searchMatch && categoryMatch && tabMatch
        })
    }, [configsArray, searchText, categoryFilter, activeTab])

    const renderValue = (value: any, configName?: string): React.ReactNode => {
        if (value === null || value === undefined) {
            return <JsonModal value={value} title={configName || 'Конфігурація'} />
        }

        if (typeof value === 'boolean') {
            return <JsonModal value={value} title={configName || 'Конфігурація'} />
        }

        if (typeof value === 'string') {
            return <JsonModal value={value} title={configName || 'Конфігурація'} />
        }

        if (typeof value === 'number') {
            return <JsonModal value={value} title={configName || 'Конфігурація'} />
        }

        if (typeof value === 'object') {
            return <JsonModal value={value} title={configName || 'Конфігурація'} />
        }

        return <JsonModal value={value} title={configName || 'Конфігурація'} />
    }

    const columns = [
        {
            title: 'Назва',
            dataIndex: 'name',
            key: 'name',
            width: '30%',
            render: (name: string) => (
                <Text strong style={{ fontSize: '12px' }}>
                    {name}
                </Text>
            ),
        },
        {
            title: 'Значення',
            dataIndex: 'value',
            key: 'value',
            width: '70%',
            render: (value: any, record: any) => renderValue(value, record.name),
        },
    ]

    return (
        <div>
            <Space style={{ marginBottom: 16, width: '100%' }} wrap>
                <Input
                    placeholder="Пошук за назвою..."
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 200 }}
                />
                <Select
                    placeholder="Категорія"
                    allowClear
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    style={{ width: 120 }}
                >
                    {categories.map((category) => (
                        <Select.Option key={category} value={category}>
                            {category}
                        </Select.Option>
                    ))}
                </Select>
                <Select placeholder="Фільтр" value={activeTab} onChange={setActiveTab} style={{ width: 120 }}>
                    <Select.Option value="all">Всі</Select.Option>
                    <Select.Option value="system">Системні</Select.Option>
                    <Select.Option value="user">Користувацькі</Select.Option>
                </Select>
                {onRefresh && (
                    <Button icon={<ReloadOutlined />} onClick={onRefresh}>
                        Оновити
                    </Button>
                )}
            </Space>

            <Table
                columns={columns}
                dataSource={filteredConfigs}
                pagination={{
                    pageSize: 15,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Всього ${total} записів`,
                }}
                size="small"
                scroll={{ x: 'max-content' }}
            />
        </div>
    )
}

export class ConfigLoaderProfiler implements DebugProfilerInterface {
    key = 'config-loader'
    name = 'Конфіги'
    icon = (<SettingOutlined />)
    priority = 80

    isEnabled(): boolean {
        return true
    }

    async getData(onRefresh?: () => void): Promise<DebugProfilerData> {
        try {
            const kernel = Kernel.getInstance()
            const configLoader = kernel.getLoader('config') as any

            if (!configLoader) {
                return {
                    summary: {
                        count: 0,
                        status: 'warning',
                        label: 'Немає даних',
                    },
                    details: {
                        custom: <div>ConfigLoader не знайдено</div>,
                    },
                }
            }

            const configs = configLoader.getConfigs ? configLoader.getConfigs() : {}
            const stats = configLoader.getConfigStats
                ? configLoader.getConfigStats()
                : {
                      totalConfigs: 0,
                      systemConfigs: 0,
                      userConfigs: 0,
                      environment: 'unknown',
                      configKeys: [],
                      systemKeys: [],
                      userKeys: [],
                  }

            if (Object.keys(configs).length === 0) {
                return {
                    summary: {
                        count: 0,
                        status: 'warning',
                        label: 'Немає конфігурацій',
                    },
                    details: {
                        custom: <div>Конфігурації не завантажено</div>,
                    },
                }
            }

            return {
                summary: {
                    count: stats.totalConfigs,
                    status: stats.totalConfigs > 0 ? 'success' : 'warning',
                    label: `${stats.totalConfigs} конфігурацій`,
                },
                details: {
                    custom: onRefresh ? (
                        <ConfigsTable configs={configs} onRefresh={onRefresh} />
                    ) : (
                        <ConfigsTable configs={configs} />
                    ),
                },
            }
        } catch (error) {
            console.error('Помилка отримання даних конфігурацій:', error)

            return {
                summary: {
                    count: 0,
                    status: 'error',
                    label: 'Помилка',
                },
                details: {
                    custom: (
                        <div style={{ color: '#ff4d4f' }}>
                            {error instanceof Error ? error.message : 'Невідома помилка'}
                        </div>
                    ),
                },
            }
        }
    }
}
